import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  Sparkles,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Compass,
  ArrowUpRight,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  ShoppingBag,
  MapPin,
  Globe,
  AlertTriangle,
  Download,
} from "lucide-react";
import { toast } from "sonner";

/**
 * /concierge — public knowledge-base landing.
 * Suggested Q&A up top, category filter chips, expandable FAQ list.
 * Deep-links via ?q=<id> auto-expand one item.
 */

function Highlight({ text, query }) {
  const q = (query || "").trim();
  // First split by URL so links stay intact, then apply highlighting to the
  // non-URL fragments.
  const URL_RE = /(https?:\/\/[^\s)]+)/g;
  const chunks = String(text || "").split(URL_RE);
  return chunks.map((chunk, ci) => {
    if (URL_RE.test(chunk)) {
      URL_RE.lastIndex = 0;
      return (
        <a
          key={`u-${ci}`}
          href={chunk}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2 break-all"
        >
          {chunk}
        </a>
      );
    }
    if (!q) return <React.Fragment key={`t-${ci}`}>{chunk}</React.Fragment>;
    const parts = chunk.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark
          key={`m-${ci}-${i}`}
          className="bg-[#D4B878]/40 text-[#E8D6A3] rounded px-0.5"
        >
          {p}
        </mark>
      ) : (
        <React.Fragment key={`x-${ci}-${i}`}>{p}</React.Fragment>
      ),
    );
  });
}

/**
 * DownloadButtons — pill-shaped brand-accented buttons that trigger a
 * platform-specific PDF install download. Rendered inline on FAQ items
 * that expose a `downloads` array (currently only signage-player-compat).
 */
function DownloadButtons({ items }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div
      data-testid="concierge-downloads"
      className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      {items.map((d) => {
        const href = `${backend}/api${d.url.replace(/^\/api/, "")}`;
        return (
          <a
            key={d.id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`concierge-download-${d.id}`}
            className="group flex items-center gap-3 p-4 rounded-lg border border-white/10 hover:border-transparent transition-all"
            style={{
              backgroundColor: `${d.accent}10`,
            }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-black"
              style={{ backgroundColor: d.accent }}
            >
              <Download size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-0.5"
                style={{ color: d.accent }}
              >
                {d.platform}
              </div>
              <div className="text-sm font-semibold text-white group-hover:text-white">
                {d.label}
              </div>
            </div>
            <ArrowUpRight
              size={14}
              className="text-white/40 group-hover:text-white shrink-0"
            />
          </a>
        );
      })}
    </div>
  );
}


/**
 * ComparisonCard — inline LG-vs-Sony toggle. Not a generic comparison, but a
 * simple 2-column model tuned for the recommended-displays FAQ item.
 */
function ComparisonCard({ c }) {
  const [active, setActive] = React.useState(c.columns?.[0]?.key || "");
  if (!c?.columns?.length) return null;
  const col = c.columns.find((x) => x.key === active) || c.columns[0];
  return (
    <div
      data-testid="concierge-comparison"
      className="mt-5 border border-white/10 rounded-lg bg-black/40 overflow-hidden"
    >
      <div className="flex" data-testid="concierge-comparison-tabs">
        {c.columns.map((x) => {
          const on = x.key === active;
          return (
            <button
              key={x.key}
              data-testid={`concierge-comp-tab-${x.key}`}
              onClick={() => setActive(x.key)}
              className={`flex-1 px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition ${
                on
                  ? "text-black font-semibold"
                  : "text-white/50 hover:text-white"
              }`}
              style={on ? { backgroundColor: x.accent || "#D4B878" } : {}}
            >
              {x.brand} · {x.model}
            </button>
          );
        })}
      </div>
      <div className="p-4">
        {col.highlight && (
          <div
            className="text-[10px] uppercase tracking-[0.22em] mb-3 font-semibold"
            style={{ color: col.accent }}
          >
            {col.highlight}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          {Object.entries(col.specs || {}).map(([k, v]) => (
            <div
              key={k}
              data-testid={`concierge-comp-spec-${col.key}-${k.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-baseline justify-between border-b border-white/[0.06] py-1.5"
            >
              <span className="text-white/50 text-[11px] uppercase tracking-[0.12em]">
                {k}
              </span>
              <span className="text-white/90 font-mono text-[12px]">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * PurchaseAssist — national retailer links + a city selector that rewrites a
 * Google Maps 'authorised dealer near {city}' template. Chose Google Maps
 * because a real per-city dealer directory is a whole separate integration.
 */
function PurchaseAssist({ p }) {
  const [city, setCity] = React.useState(p.cities?.[0] || "");
  const mapUrl = (p.local_search_template || "").replace(
    "{city}",
    encodeURIComponent(city || "you"),
  );
  return (
    <div
      data-testid="concierge-purchase-assist"
      className="mt-4 border border-[#D4B878]/20 rounded-lg bg-[#D4B878]/[0.03] p-4 space-y-3"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4B878] font-semibold">
        <ShoppingBag size={11} /> Where to buy
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {(p.national || []).map((r) => (
          <a
            key={r.retailer}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`concierge-buy-${r.retailer.toLowerCase().replace(/\s+/g, "-")}`}
            className="group p-3 bg-black/40 border border-white/10 hover:border-[#D4B878]/60 rounded-md transition-all"
          >
            <div className="text-sm text-white font-medium flex items-center justify-between">
              {r.retailer}
              <ArrowUpRight
                size={13}
                className="text-white/30 group-hover:text-[#D4B878]"
              />
            </div>
            <div className="text-[11px] text-white/50 mt-1">{r.note}</div>
          </a>
        ))}
      </div>
      {p.cities?.length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
          <MapPin size={11} className="text-white/40 shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/50">
            Local dealers for
          </span>
          <select
            data-testid="concierge-buy-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="bg-transparent border border-white/15 text-xs text-white rounded px-2 py-1 focus:outline-none focus:border-[#D4B878]"
          >
            {p.cities.map((c) => (
              <option key={c} value={c} className="bg-[#14110E]">
                {c}
              </option>
            ))}
          </select>
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="concierge-buy-local"
            className="ml-auto text-xs text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2"
          >
            Find dealer →
          </a>
        </div>
      )}
    </div>
  );
}



export default function Concierge() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null); // null = all
  const [expanded, setExpanded] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);
  const [feedback, setFeedback] = useState({}); // { [id]: {useful, not_useful, your_vote} }
  const [topMovers, setTopMovers] = useState([]); // items with >5 not_useful
  const [params, setParams] = useSearchParams();
  const editorMode = params.get("editor") === "1";
  const countryOverride = params.get("country") || ""; // user-forced country

  useEffect(() => {
    (async () => {
      try {
        const faqUrl = countryOverride
          ? `/concierge/faq?country=${encodeURIComponent(countryOverride)}`
          : "/concierge/faq";
        const requests = [
          api.get(faqUrl),
          api.get("/concierge/feedback"),
        ];
        if (editorMode) {
          requests.push(api.get("/concierge/feedback/top-movers?threshold=5"));
        }
        const [faq, fb, movers] = await Promise.all(requests);
        setData(faq.data);
        setFeedback(fb.data.totals || {});
        if (movers) setTopMovers(movers.data.items || []);
      } catch (e) {
        setError("Couldn't load the knowledge base.");
      }
    })();
  }, [editorMode, countryOverride]);

  // Deep-link support: ?q=item-id or ?cat=platform
  useEffect(() => {
    if (!data) return;
    const deep = params.get("q");
    const cat = params.get("cat");
    if (deep) {
      setExpanded(new Set([deep]));
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="concierge-item-${deep}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
    if (cat) setActiveCat(cat);
  }, [data, params]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.items;
    if (activeCat) items = items.filter((f) => f.category === activeCat);
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (f) =>
        f.question.toLowerCase().includes(query) ||
        f.answer.toLowerCase().includes(query) ||
        (f.tags || []).some((t) => t.toLowerCase().includes(query)),
    );
  }, [data, q, activeCat]);

  const suggested = useMemo(() => {
    if (!data) return [];
    const ids = new Set(data.suggested_ids || []);
    return data.items.filter((f) => ids.has(f.id));
  }, [data]);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/concierge?q=${id}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("Deep link copied");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const vote = async (id, kind) => {
    try {
      const r = await api.post(`/concierge/faq/${id}/feedback`, { vote: kind });
      setFeedback((prev) => ({
        ...prev,
        [id]: {
          useful: r.data.useful,
          not_useful: r.data.not_useful,
          your_vote: r.data.your_vote,
        },
      }));
      if (r.data.changed) {
        toast.success(kind === "useful" ? "Thanks for the feedback" : "Noted — we'll improve this");
      }
    } catch (e) {
      toast.error("Couldn't record your vote");
    }
  };

  if (error) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center text-white/60" data-testid="concierge-error">
          {error}
        </div>
      </PublicShell>
    );
  }
  if (!data) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center text-white/40">Loading concierge…</div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div data-testid="concierge-page" className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-4">
            <Compass size={12} /> Concierge · Knowledge Base
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            Everything about
            <br />
            <span className="text-[#D4B878]">programmable physical media.</span>
          </h1>
          <p className="mt-6 text-white/60 text-base max-w-2xl leading-relaxed">
            Curated answers drawn from the Mirrorloops master reference. No
            login required — pick a suggested question, browse a category, or
            search below.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
          />
          <Input
            data-testid="concierge-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search questions, topics, tags…"
            className="pl-11 h-12 bg-white/[0.03] border-white/10 focus:border-[#D4B878] text-white placeholder:text-white/30 rounded-full"
          />
        </div>

        {/* Country-localisation ribbon */}
        {data && data.country && (data.localized_ids || []).length > 0 && (
          <div
            data-testid="concierge-country-ribbon"
            className="mb-6 flex items-center gap-3 flex-wrap p-3 border border-[#00E5FF]/25 bg-[#00E5FF]/[0.05] rounded-lg"
          >
            <Globe size={14} className="text-[#00E5FF] shrink-0" />
            <div className="text-[12px] text-white/80 leading-relaxed">
              Showing{" "}
              <span
                data-testid="concierge-country-code"
                className="text-[#00E5FF] font-semibold font-mono"
              >
                {data.country}
              </span>
              -localized pricing & retailer links on{" "}
              <span className="text-white font-semibold">
                {data.localized_ids.length}
              </span>{" "}
              answer{data.localized_ids.length === 1 ? "" : "s"}. Not your
              region?{" "}
              <select
                data-testid="concierge-country-picker"
                value={countryOverride || data.country || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = new URLSearchParams(params);
                  if (v) next.set("country", v);
                  else next.delete("country");
                  setParams(next);
                }}
                className="bg-black/60 border border-white/10 rounded text-white text-[11px] font-mono px-2 py-0.5 ml-1"
              >
                <option value="">Auto-detect</option>
                <option value="IN">India (IN)</option>
                <option value="GB">United Kingdom (GB)</option>
                <option value="US">United States (US)</option>
                <option value="AE">UAE (AE)</option>
                <option value="AU">Australia (AU)</option>
              </select>
            </div>
          </div>
        )}

        {/* Editor mode: top-mover ping */}
        {editorMode && topMovers.length > 0 && (
          <div
            data-testid="concierge-top-movers"
            className="mb-8 border border-red-400/30 bg-red-500/[0.06] rounded-lg p-4"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-red-300 font-semibold mb-3">
              <AlertTriangle size={11} />
              Content review · {topMovers.length}{" "}
              answer{topMovers.length === 1 ? "" : "s"} above threshold
            </div>
            <div className="space-y-1.5">
              {topMovers.map((m) => (
                <button
                  key={m.id}
                  data-testid={`concierge-top-mover-${m.id}`}
                  onClick={() => {
                    setExpanded(new Set([m.id]));
                    setParams({ q: m.id, editor: "1" });
                    setTimeout(() => {
                      const el = document.querySelector(
                        `[data-testid="concierge-item-${m.id}"]`,
                      );
                      if (el)
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 100);
                  }}
                  className="w-full text-left flex items-center gap-3 p-2 bg-black/40 hover:bg-black/60 border border-white/10 rounded transition-colors"
                >
                  <ThumbsDown size={11} className="text-red-400 shrink-0" />
                  <span className="text-[11px] font-mono text-red-300 shrink-0">
                    {m.not_useful}
                  </span>
                  <span className="text-[13px] text-white/80 flex-1 truncate">
                    {m.question}
                  </span>
                  <span className="text-[9px] font-mono text-white/40">
                    net {m.net >= 0 ? "+" : ""}
                    {m.net}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Q&A quick picks */}
        {!q && !activeCat && (
          <div className="mb-12" data-testid="concierge-suggested">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/50 mb-4">
              <Sparkles size={11} className="text-[#D4B878]" />
              Suggested for you
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggested.map((f) => {
                const catMeta = data.categories.find((c) => c.key === f.category);
                return (
                  <button
                    key={f.id}
                    data-testid={`concierge-suggested-${f.id}`}
                    onClick={() => {
                      setExpanded(new Set([f.id]));
                      setParams({ q: f.id });
                      setTimeout(() => {
                        const el = document.querySelector(
                          `[data-testid="concierge-item-${f.id}"]`,
                        );
                        if (el)
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 100);
                    }}
                    className="group text-left p-4 bg-white/[0.03] border border-white/10 hover:border-[#D4B878]/50 rounded-lg transition-all"
                  >
                    <div
                      className="text-[9px] uppercase tracking-[0.22em] mb-2 font-semibold"
                      style={{ color: catMeta?.accent || "#D4B878" }}
                    >
                      {catMeta?.label || f.category}
                    </div>
                    <div className="text-sm text-white/90 font-medium leading-snug group-hover:text-white">
                      {f.question}
                    </div>
                    <ArrowUpRight
                      size={14}
                      className="text-white/30 group-hover:text-[#D4B878] mt-2 transition-colors"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="concierge-categories">
          <button
            data-testid="concierge-cat-all"
            onClick={() => {
              setActiveCat(null);
              setParams({});
            }}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] rounded-full border transition-all ${
              !activeCat
                ? "bg-white text-black border-white"
                : "bg-white/[0.03] text-white/60 border-white/15 hover:border-white/40"
            }`}
          >
            All · {data.total}
          </button>
          {data.categories.map((c) => (
            <button
              key={c.key}
              data-testid={`concierge-cat-${c.key}`}
              onClick={() => {
                setActiveCat(c.key === activeCat ? null : c.key);
                setParams({ cat: c.key });
              }}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] rounded-full border transition-all flex items-center gap-1.5 ${
                activeCat === c.key
                  ? "text-black border-transparent"
                  : "bg-white/[0.03] text-white/60 border-white/15 hover:border-white/40"
              }`}
              style={
                activeCat === c.key ? { backgroundColor: c.accent } : {}
              }
            >
              {c.label} · {c.count}
            </button>
          ))}
        </div>

        {/* FAQ list */}
        <div className="space-y-2" data-testid="concierge-faq-list">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-white/40 border border-white/10 rounded-lg">
              No results for "{q}". Try a different search term.
            </div>
          )}
          {filtered.map((f) => {
            const isOpen = expanded.has(f.id);
            const catMeta = data.categories.find((c) => c.key === f.category);
            return (
              <div
                key={f.id}
                data-testid={`concierge-item-${f.id}`}
                className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]"
              >
                <button
                  onClick={() => toggleExpand(f.id)}
                  data-testid={`concierge-toggle-${f.id}`}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex-1 text-left min-w-0">
                    <div
                      className="text-[9px] uppercase tracking-[0.22em] mb-1 font-semibold flex items-center gap-2"
                      style={{ color: catMeta?.accent || "#D4B878" }}
                    >
                      <span>{catMeta?.label || f.category}</span>
                      {f.country_hint && (
                        <span
                          data-testid={`faq-country-hint-${f.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full text-[#00E5FF] border border-[#00E5FF]/30 bg-[#00E5FF]/[0.08] font-mono normal-case tracking-normal"
                        >
                          <Globe size={9} />
                          <span className="text-[9px]">
                            Localized for {f.country_hint}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="text-base font-medium text-white leading-snug">
                      <Highlight text={f.question} query={q} />
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronUp size={18} className="text-white/40 shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-white/40 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div
                    data-testid={`concierge-answer-${f.id}`}
                    className="px-5 pb-5 -mt-1"
                  >
                    <div className="border-t border-white/10 pt-4 text-[15px] text-white/75 leading-relaxed">
                      <Highlight text={f.answer} query={q} />
                    </div>
                    {f.downloads && <DownloadButtons items={f.downloads} />}
                    {f.comparison && <ComparisonCard c={f.comparison} />}
                    {f.purchase_options && (
                      <PurchaseAssist p={f.purchase_options} />
                    )}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[10px] text-white/40">
                      <div className="flex flex-wrap gap-1.5">
                        {(f.tags || []).map((t) => (
                          <span
                            key={t}
                            className="px-2 py-0.5 bg-white/[0.04] border border-white/10 rounded-full uppercase tracking-[0.14em]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const fb = feedback[f.id] || {};
                          const mine = fb.your_vote;
                          return (
                            <>
                              <span className="uppercase tracking-[0.18em] text-white/50">
                                Was this useful?
                              </span>
                              <button
                                onClick={() => vote(f.id, "useful")}
                                data-testid={`concierge-vote-useful-${f.id}`}
                                title="Yes, useful"
                                className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
                                  mine === "useful"
                                    ? "border-[#CCFF00]/60 text-[#CCFF00] bg-[#CCFF00]/10"
                                    : "border-white/15 text-white/50 hover:border-[#CCFF00]/40 hover:text-[#CCFF00]"
                                }`}
                              >
                                <ThumbsUp size={11} />
                                <span
                                  data-testid={`concierge-count-useful-${f.id}`}
                                  className="font-mono"
                                >
                                  {fb.useful || 0}
                                </span>
                              </button>
                              <button
                                onClick={() => vote(f.id, "not_useful")}
                                data-testid={`concierge-vote-notuseful-${f.id}`}
                                title="Not quite"
                                className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
                                  mine === "not_useful"
                                    ? "border-red-400/60 text-red-300 bg-red-500/10"
                                    : "border-white/15 text-white/50 hover:border-red-400/40 hover:text-red-300"
                                }`}
                              >
                                <ThumbsDown size={11} />
                                <span
                                  data-testid={`concierge-count-notuseful-${f.id}`}
                                  className="font-mono"
                                >
                                  {fb.not_useful || 0}
                                </span>
                              </button>
                              <button
                                onClick={() => copyLink(f.id)}
                                data-testid={`concierge-copy-${f.id}`}
                                className="flex items-center gap-1.5 uppercase tracking-[0.18em] text-white/50 hover:text-[#D4B878] transition-colors"
                              >
                                {copiedId === f.id ? (
                                  <>
                                    <Check size={11} /> Copied
                                  </>
                                ) : (
                                  <>
                                    <Copy size={11} /> Share
                                  </>
                                )}
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[11px] text-white/40">
          <div className="flex items-center gap-2">
            <BookOpen size={12} /> {data.total} answers · curated from the
            Mirrorloops master reference
          </div>
          <div className="flex gap-4">
            <Link to="/explore" className="hover:text-white transition-colors">
              Explore product →
            </Link>
            <Link to="/register" className="hover:text-[#D4B878] transition-colors">
              Start free →
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
